/**
 * The generator's scratch map: one byte of flags per square metre. Everything that lays
 * something down stamps it here, and everything that looks for room asks here, so nothing
 * ends up on a road, in a ravine, under water, in a gate or on top of something else.
 *
 * Stamps are conservative (every cell the shape touches), which is what lets the generator
 * promise "this cell is free on the sim's nav grid" without building that grid.
 */

export const FLAG = {
  solid: 1,
  road: 2,
  trench: 4,
  water: 8,
  mine: 16,
  /** Reserved open ground: gates, crossings, pod clearings, the pickup, the start, soldiers' posts. */
  keep: 32,
  /** Patrol legs and the corridors that must stay walkable. */
  path: 64,
  /** Blue-noise exclusion around scattered cover. */
  cover: 128,
} as const;

export const ALL_FLAGS = 0xff;

export class Mask {
  readonly width: number;
  readonly height: number;
  readonly cells: Uint8Array;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.cells = new Uint8Array(width * height);
  }

  /** Flags at a world position; off the map everything is set. */
  at(x: number, z: number): number {
    const cx = Math.floor(x);
    const cz = Math.floor(z);
    if (cx < 0 || cz < 0 || cx >= this.width || cz >= this.height) return ALL_FLAGS;
    return this.cells[cz * this.width + cx] ?? ALL_FLAGS;
  }

  rect(x0: number, z0: number, x1: number, z1: number, flag: number): void {
    const ax = Math.max(0, Math.floor(x0));
    const bx = Math.min(this.width - 1, Math.floor(x1));
    const az = Math.max(0, Math.floor(z0));
    const bz = Math.min(this.height - 1, Math.floor(z1));
    for (let z = az; z <= bz; z++) {
      for (let x = ax; x <= bx; x++) {
        const i = z * this.width + x;
        this.cells[i] = (this.cells[i] ?? 0) | flag;
      }
    }
  }

  circle(x: number, z: number, r: number, flag: number): void {
    const reach = r + 0.71;
    const ax = Math.max(0, Math.floor(x - reach));
    const bx = Math.min(this.width - 1, Math.floor(x + reach));
    const az = Math.max(0, Math.floor(z - reach));
    const bz = Math.min(this.height - 1, Math.floor(z + reach));
    for (let cz = az; cz <= bz; cz++) {
      for (let cx = ax; cx <= bx; cx++) {
        const dx = cx + 0.5 - x;
        const dz = cz + 0.5 - z;
        if (dx * dx + dz * dz > reach * reach) continue;
        const i = cz * this.width + cx;
        this.cells[i] = (this.cells[i] ?? 0) | flag;
      }
    }
  }

  /** A swept circle along a polyline: roads, trenches, rivers, patrol legs. */
  line(points: ReadonlyArray<readonly [number, number]>, halfWidth: number, flag: number): void {
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const b = points[i];
      if (!a || !b) continue;
      const dx = b[0] - a[0];
      const dz = b[1] - a[1];
      const steps = Math.max(1, Math.ceil(Math.sqrt(dx * dx + dz * dz) / 0.75));
      for (let s = 0; s <= steps; s++) this.circle(a[0] + (dx * s) / steps, a[1] + (dz * s) / steps, halfWidth, flag);
    }
  }

  /** Does any cell the rectangle touches carry one of `flags`? Leaving the map counts as a hit. */
  hitsRect(x0: number, z0: number, x1: number, z1: number, flags: number): boolean {
    if (x0 < 0 || z0 < 0 || x1 >= this.width || z1 >= this.height) return true;
    for (let z = Math.floor(z0); z <= Math.floor(z1); z++) {
      for (let x = Math.floor(x0); x <= Math.floor(x1); x++) {
        if (((this.cells[z * this.width + x] ?? ALL_FLAGS) & flags) !== 0) return true;
      }
    }
    return false;
  }

  hitsCircle(x: number, z: number, r: number, flags: number): boolean {
    return this.hitsRect(x - r, z - r, x + r, z + r, flags);
  }

  /** Is the straight line between two points free of `flags`? A cheap field-of-fire check. */
  clearLine(ax: number, az: number, bx: number, bz: number, flags: number): boolean {
    const dx = bx - ax;
    const dz = bz - az;
    const steps = Math.max(1, Math.ceil(Math.sqrt(dx * dx + dz * dz) / 0.5));
    for (let s = 0; s <= steps; s++) {
      if ((this.at(ax + (dx * s) / steps, az + (dz * s) / steps) & flags) !== 0) return false;
    }
    return true;
  }
}
